// IN-PORTAL CAMERA. Tapping "Take photo" used to hand off to the phone's own
// camera app; Android can close the portal in the background while that app
// is open, and the photo never comes back. This shows the live camera INSIDE
// the portal page instead, so nothing leaves the screen. The captured picture
// is handed on as an ordinary photo File, so stamping, GPS and saving are
// unchanged. If the phone won't allow it, it falls back to the phone camera.
import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

// Open the camera over the whole page. onFiles gets [File] like a file input
// would; fallbackId is the hidden <input capture> to use when the live camera
// isn't available.
export function openInAppCamera(onFiles, fallbackId) {
  const fallback = () => { const el = fallbackId && document.getElementById(fallbackId); if (el) el.click(); };
  const canLive = typeof navigator !== "undefined" && navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function";
  if (!canLive) { fallback(); return; }
  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = createRoot(host);
  const close = () => { setTimeout(() => { root.unmount(); host.remove(); }, 0); };
  root.render(
    <InAppCamera
      onCapture={(file) => { close(); onFiles([file]); }}
      onFallback={() => { close(); fallback(); }}
      onClose={close} />
  );
}

export default function InAppCamera({ onCapture, onFallback, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let stopped = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1440 } }, audio: false,
        });
        if (stopped) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) { v.srcObject = stream; await v.play().catch(() => {}); setReady(true); }
      } catch (e) {
        // Camera blocked or unavailable here — use the phone camera instead.
        if (!stopped) setErr("The camera couldn't open here.");
      }
    })();
    return () => { stopped = true; if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
  }, []);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || busy) return;
    setBusy(true);
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => {
      setBusy(false);
      if (!blob) { setErr("That picture didn't work. Try again."); return; }
      const now = Date.now();
      const file = new File([blob], `camera-${now}.jpg`, { type: "image/jpeg", lastModified: now });
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      onCapture(file);
    }, "image/jpeg", 0.9);
  };

  return (
    <div data-testid="in-app-camera" style={{ position: "fixed", inset: 0, zIndex: 100000, background: "#000", display: "flex", flexDirection: "column" }}>
      <video ref={videoRef} playsInline muted autoPlay style={{ flex: 1, width: "100%", objectFit: "cover", background: "#000" }} />
      {err && (
        <div style={{ position: "absolute", top: "40%", left: 16, right: 16, textAlign: "center", color: "#fff", fontSize: "1.05rem" }}>
          {err}
          <div><button type="button" className="btn-primary" style={{ marginTop: "0.8rem" }} onClick={onFallback}>Use phone camera</button></div>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px 28px", background: "#000" }}>
        <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: "#fff", fontSize: "1.05rem", minWidth: 80, minHeight: 48 }}>Cancel</button>
        <button type="button" data-testid="camera-shutter" aria-label="Take picture" disabled={!ready || busy} onClick={snap}
          style={{ width: 76, height: 76, borderRadius: 38, border: "5px solid #fff", background: ready ? "#fff" : "#666", boxShadow: "0 0 0 3px #000 inset" }} />
        <button type="button" onClick={onFallback} style={{ background: "none", border: "none", color: "#fff", fontSize: "0.85rem", minWidth: 80, minHeight: 48, textDecoration: "underline" }}>Phone camera</button>
      </div>
    </div>
  );
}
